import type { FC } from 'react';

import { SECTION_IDS } from '@/features/home/constants/home.constants';
import { SITE_CONTAINER } from '@/features/home/constants/homeStyles.constants';
import type { CapabilityCard, SectionIntro } from '@/types/home.types';

interface CapabilitiesSectionProps {
  intro: SectionIntro;
  cards: CapabilityCard[];
}

const TAG_TONE: Record<CapabilityCard['tone'], string> = {
  accent: 'bg-site-accent-soft text-site-accent-ink',
  neutral: 'bg-site-surface text-site-ink',
};

const CapabilitiesSection: FC<CapabilitiesSectionProps> = ({ intro, cards }) => (
  <section id={SECTION_IDS.what} className="scroll-mt-20 bg-site-bg">
    <div className={SITE_CONTAINER}>
      <hr className="h-0.5 border-0 bg-site-line" />

      <div className="py-14 md:py-[72px]">
        <span className="block font-site text-[13px] tracking-[0.08em] text-site-accent-deep uppercase">
          {intro.eyebrow}
        </span>

        <h2 className="mt-4 max-w-[22ch] font-site text-[clamp(30px,3.6vw,48px)] leading-[1.08] font-extrabold tracking-[-0.02em]">
          {intro.title}
        </h2>

        <p className="mt-4 max-w-[52ch] font-site text-[17px] leading-[1.55] text-site-ink/80">
          {intro.body}
        </p>

        <div className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] gap-0.5 border-2 border-site-ink bg-site-ink">
          {cards.map((card) => (
            <article key={card.index} className="flex flex-col gap-3 bg-site-bg p-7">
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`inline-flex items-center rounded-sm px-2.5 py-[3px] font-site text-[11px] whitespace-nowrap ${TAG_TONE[card.tone]}`}
                >
                  {card.tag}
                </span>
                <span className="font-site text-[15px] font-extrabold">{card.index}</span>
              </div>

              <h3 className="font-site text-[26px] font-extrabold tracking-[-0.01em]">
                {card.title}
              </h3>

              <p className="font-site text-base leading-[1.5] text-site-ink/80">{card.body}</p>

              <p className="mt-auto border-t-2 border-site-line pt-3.5 font-site text-[14px] font-semibold">
                {card.example}
              </p>
            </article>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default CapabilitiesSection;
